import React, { useCallback, useEffect, useState } from 'react';

import styled from 'styled-components';

import Modal from 'components/lib/Modal';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { useAppSelector } from 'store/hook';
import { getCommunitiesByIds, getCommunityById } from 'utils/helpers/communityMap';

import { ACTIONS, STEPS } from './constants';
import { SwotCompetitorAnalysisContext } from './SwotCompetitorAnalysisContext';
import SwotCompetitorAnalysisModalBody from './SwotCompetitorAnalysisModalBody';
import SwotCompetitorAnalysisModalFooter from './SwotCompetitorAnalysisModalFooter';
import SwotCompetitorAnalysisModalHeader from './SwotCompetitorAnalysisModalHeader';
import { STATUS } from '../../../../constants';
import EditSwotDataModal from '../EditSwotDataModal/EditSwotDataModal';

type Props = {
  open: boolean;
  onClose: () => void;
};

const StyledModal = styled(Modal)`
  &&& .ant-modal-body {
    padding: 0;
  }
`;

const SwotCompetitorAnalysisModal = ({ open, onClose }: Props) => {
  const { selectedCommunity, communityMap } = useAppSelector((state) => state.global);
  const [step, setStep] = useState<number>(STEPS.COMPETITOR_SELECTOR); // setting it STEPS.COMPETITOR_SELECTOR because invite to edit and view options are not availbale yet
  const [selectedAction, setSelectedAction] = useState<string>(ACTIONS.EDIT_DATA);
  const [selectedCompetitor, setSelectedCompetitor] = useState<number>(0);
  const [communityOptions, setCommunityOptions] = useState<any[]>([]);

  const [loading, setLoading] = useState<string>(STATUS.IDLE);

  const [showInsertSwotDataModal, setShowInsertSwotDataModal] = useState(false);
  const [competitorId, setCompetitorId] = useState<number | null>(null);

  const closeModal = useCallback(() => setShowInsertSwotDataModal(false), [setShowInsertSwotDataModal]);

  const handleClose = () => {
    setStep(STEPS.COMPETITOR_SELECTOR);
    setSelectedAction(ACTIONS.EDIT_DATA);
    setSelectedCompetitor(0);

    onClose();
  };

  const onCompetitorSelect = (id: number) => {
    setCompetitorId(id);
    setShowInsertSwotDataModal(true);
    handleClose();
  };

  useEffect(() => {
    const loadCommunityOptions = () => {
      const selectedCommunityId = selectedCommunity?.id;

      const currentCommunity = getCommunityById(communityMap, selectedCommunityId);
      if (!selectedCommunityId || !currentCommunity) {
        setCommunityOptions([]);
        setLoading(STATUS.LOADED);
        return;
      }

      const isCompetitor = selectedCommunity?.type === COMPANY_COMMUNITY_RELATION_TYPE.COMPETITOR;

      const options = getCommunitiesByIds(
        communityMap,
        isCompetitor ? currentCommunity.communities : currentCommunity.competitors
      ).filter((community) => Boolean(community));

      if (options.length === 1 && options[0]) {
        if (isCompetitor && selectedCommunityId) {
          onCompetitorSelect(selectedCommunityId);
        } else {
          onCompetitorSelect(options[0].id);
        }
        return;
      }

      setCommunityOptions(options);
      setLoading(STATUS.LOADED);
    };

    if (open) {
      loadCommunityOptions();
    }
  }, [open]);

  return (
    <SwotCompetitorAnalysisContext.Provider
      value={{
        step,
        selectedAction,
        selectedCompetitor,
        communityOptions,
        setStep,
        setSelectedAction,
        setSelectedCompetitor,
        handleClose,
        onCompetitorSelect,
      }}
    >
      {loading == STATUS.LOADED && (
        <StyledModal open={open} onCancel={handleClose} footer={<SwotCompetitorAnalysisModalFooter />} width={540}>
          <SwotCompetitorAnalysisModalHeader />
          <SwotCompetitorAnalysisModalBody />
        </StyledModal>
      )}

      {showInsertSwotDataModal && (
        <EditSwotDataModal onClose={closeModal} open={showInsertSwotDataModal} competitorId={competitorId} />
      )}
    </SwotCompetitorAnalysisContext.Provider>
  );
};

export default SwotCompetitorAnalysisModal;
